import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Badge
} from '@mui/material';
import {
  ExpandMore,
  FilterList,
  Description,
  Build,
  ElectricalServices,
  Water,
  Engineering,
  Settings,
  Info,
  Close,
  Search,
  Download,
  Folder,
  Tab as TabIcon
} from '@mui/icons-material';
import { 
  Tabs,
  Tab
} from '@mui/material';
import EquipmentDocumentViewer from '../Documents/EquipmentDocumentViewer';
import DocumentSearch from '../Documents/DocumentSearch';
import DocumentViewer from '../Documents/DocumentViewer';
import { Document } from '../../services/documentsService';

interface Site {
  id: number;
  name: string;
  code: string;
  site_type: string;
}

interface SiteUnit {
  id: number;
  name: string;
  unit_number: string;
  site: number;
}

interface GlobalSystem {
  id: number;
  name: string;
  system_code: string;
  system_type: string;
}

interface SiteKKSSubsystem {
  id: number;
  name: string;
  kks_code: string;
  subsystem_code: string;
  site_unit: number;
  global_system: GlobalSystem;
  is_critical: boolean;
}

interface EquipmentUnitCode {
  id: number;
  name: string;
  unit_code: string;
  category: string;
}

interface Equipment {
  id: number;
  name: string;
  kks_code: string;
  site_name: string;
  site_code: string;
  unit_name: string;
  subsystem_name: string;
  equipment_unit_name: string;
  status: string;
  criticality: string;
  manufacturer: string;
  model: string;
  energizable: boolean;
  requires_isolation: boolean;
  hierarchy_level: number;
  parent_equipment: number | null;
  energy_sources_count: number;
  has_high_risk_energy: boolean;
}

interface EquipmentDocument {
  id: number;
  title: string;
  document_type: string;
  document_number: string;
  version: string;
  file: string;
  external_link: string;
}

interface EquipmentDetail extends Equipment {
  description: string;
  serial_number: string;
  voltage_rating: string;
  current_rating: string;
  power_rating: string;
  pressure_rating: string;
  flow_rating: string;
  technical_drawings: string;
  operating_procedures: string;
  documents: EquipmentDocument[];
  child_equipment: Equipment[];
  energy_sources: any[];
  isolation_methods: any[];
}

interface SystemGroup {
  kks_code: string;
  name: string;
  is_critical: boolean;
  subsystems: SiteKKSSubsystem[];
  equipment: Equipment[];
}

interface PlantInformationProps {
  selectedSiteId: number;
  siteName: string;
}

const PlantInformation: React.FC<PlantInformationProps> = ({ selectedSiteId, siteName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Data state
  const [siteUnits, setSiteUnits] = useState<SiteUnit[]>([]);
  const [subsystems, setSubsystems] = useState<SiteKKSSubsystem[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [equipmentTypes, setEquipmentTypes] = useState<EquipmentUnitCode[]>([]);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEquipmentType, setSelectedEquipmentType] = useState('');
  const [selectedSystemType, setSelectedSystemType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCriticality, setSelectedCriticality] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Detail modal state
  const [selectedEquipment, setSelectedEquipment] = useState<EquipmentDetail | null>(null);
  const [equipmentDetailOpen, setEquipmentDetailOpen] = useState(false);
  
  // Tab state
  const [currentTab, setCurrentTab] = useState(0);
  
  // Hierarchy state
  const [expandedUnits, setExpandedUnits] = useState<Set<number>>(new Set());
  const [expandedSystems, setExpandedSystems] = useState<Set<string>>(new Set());
  const [expandedSubsystems, setExpandedSubsystems] = useState<Set<number>>(new Set());
  
  // Document viewing state
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);

  useEffect(() => {
    fetchPlantData();
  }, [selectedSiteId]);

  const fetchPlantData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🏭 PlantInformation: Fetching data for site:', selectedSiteId);
      
      // Fetch site units
      const unitsResponse = await fetch(`/api/v1/sites/site-units/?site_id=${selectedSiteId}`, {
        credentials: 'include'
      });
      
      if (!unitsResponse.ok) {
        throw new Error('Failed to fetch site units');
      }
      
      const unitsData = await unitsResponse.json();
      
      // Handle both array and paginated response formats
      const unitsArray = Array.isArray(unitsData) 
        ? unitsData 
        : (unitsData?.results || []);
      
      setSiteUnits(unitsArray);
      
      // Fetch subsystems
      const subsystemsResponse = await fetch(`/api/v1/sites/site-kks-subsystems/?site_id=${selectedSiteId}`, {
        credentials: 'include'
      });
      
      if (!subsystemsResponse.ok) {
        throw new Error('Failed to fetch subsystems');
      }
      
      const subsystemsData = await subsystemsResponse.json();
      
      // Handle both array and paginated response formats
      const subsystemsArray = Array.isArray(subsystemsData) 
        ? subsystemsData 
        : (subsystemsData?.results || []);
      
      setSubsystems(subsystemsArray);
      
      // Fetch equipment
      const equipmentResponse = await fetch(`/api/v1/sites/equipment/?site_id=${selectedSiteId}`, {
        credentials: 'include'
      });
      
      if (!equipmentResponse.ok) {
        throw new Error('Failed to fetch equipment');
      }
      
      const equipmentData = await equipmentResponse.json();
      
      // Handle both array and paginated response formats
      const equipmentArray = Array.isArray(equipmentData) 
        ? equipmentData 
        : (equipmentData?.results || []);
      
      setEquipment(equipmentArray);
      
      // Fetch equipment types
      const typesResponse = await fetch('/api/v1/sites/global-equipment-unit-codes/', {
        credentials: 'include'
      });
      
      if (!typesResponse.ok) {
        throw new Error('Failed to fetch equipment types');
      }
      
      const typesData = await typesResponse.json();
      
      // Handle both array and paginated response formats
      const typesArray = Array.isArray(typesData) 
        ? typesData 
        : (typesData?.results || []);
      
      setEquipmentTypes(typesArray);
      
      console.log('✅ PlantInformation: Data loaded successfully');
      
    } catch (err) {
      console.error('❌ PlantInformation: Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load plant data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipmentDetail = async (equipmentId: number) => {
    try {
      const response = await fetch(`/api/v1/sites/equipment/${equipmentId}/`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch equipment details');
      }
      
      const data = await response.json();
      setSelectedEquipment(data);
      setEquipmentDetailOpen(true);
      
    } catch (err) {
      console.error('❌ PlantInformation: Error loading equipment detail:', err);
      setError(err instanceof Error ? err.message : 'Failed to load equipment details');
    }
  };

  const filteredEquipment = (Array.isArray(equipment) ? equipment : []).filter(item => {
    // Text search
    if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !item.kks_code.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !(item.manufacturer || '').toLowerCase().includes(searchTerm.toLowerCase()) &&
        !(item.model || '').toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Equipment type filter
    if (selectedEquipmentType && item.equipment_unit_name !== selectedEquipmentType) {
      return false;
    }
    
    // Status filter
    if (selectedStatus && item.status !== selectedStatus) {
      return false;
    }
    
    // Criticality filter
    if (selectedCriticality && item.criticality !== selectedCriticality) {
      return false;
    }
    
    return true;
  });

  // Build hierarchical structure: Unit -> System Group (MEA00) -> Subsystem (MEA20) -> Equipment
  const handleDocumentSelect = (document: Document) => {
    setSelectedDocument(document);
    setDocumentViewerOpen(true);
  };

  const handleCloseDocumentViewer = () => {
    setDocumentViewerOpen(false);
    setSelectedDocument(null);
  };

  const buildHierarchicalStructure = () => {
    const unitGroups: Record<number, Record<string, SystemGroup>> = {};
    
    // First, group subsystems by their system parent and unit
    subsystems.forEach(subsystem => {
      const unitId = subsystem.site_unit;
      const parts = subsystem.kks_code.split('-');
      
      // Create system parent code (e.g., CLD-01-MEA20 -> CLD-01-MEA00)
      if (parts.length >= 3) {
        const lastPart = parts[parts.length - 1];
        if (lastPart.match(/^[A-Z]{3}\d{2}$/)) {
          const letters = lastPart.substring(0, 3);
          const systemCode = parts.slice(0, -1).concat(`${letters}00`).join('-');
          
          if (!unitGroups[unitId]) {
            unitGroups[unitId] = {};
          }
          
          if (!unitGroups[unitId][systemCode]) {
            unitGroups[unitId][systemCode] = {
              kks_code: systemCode,
              name: `${letters} System`, // Default name, will be updated if we find actual equipment
              is_critical: false,
              subsystems: [],
              equipment: []
            };
          }
          
          unitGroups[unitId][systemCode].subsystems.push(subsystem);
          if (subsystem.is_critical) {
            unitGroups[unitId][systemCode].is_critical = true;
          }
        }
      }
    });
    
    // Then, add equipment to the appropriate system groups
    filteredEquipment.forEach(item => {
      const subsystem = subsystems.find(s => s.kks_code === item.kks_code.split('-').slice(0, 3).join('-'));
      if (!subsystem) return;
      
      const unitId = subsystem.site_unit;
      const parts = subsystem.kks_code.split('-');
      
      if (parts.length >= 3) {
        const lastPart = parts[parts.length - 1];
        if (lastPart.match(/^[A-Z]{3}\d{2}$/)) {
          const letters = lastPart.substring(0, 3);
          const systemCode = parts.slice(0, -1).concat(`${letters}00`).join('-');
          
          if (unitGroups[unitId] && unitGroups[unitId][systemCode]) {
            unitGroups[unitId][systemCode].equipment.push(item);
            
            // Update system name if we find equipment with that exact system code
            if (item.kks_code === systemCode) {
              unitGroups[unitId][systemCode].name = item.name;
            }
          }
        }
      }
    });
    
    return unitGroups;
  };
  
  const hierarchicalStructure = buildHierarchicalStructure();

  const toggleUnit = (unitId: number) => {
    const newExpanded = new Set(expandedUnits);
    if (newExpanded.has(unitId)) {
      newExpanded.delete(unitId);
    } else {
      newExpanded.add(unitId);
    }
    setExpandedUnits(newExpanded);
  };

  const toggleSystem = (systemCode: string) => {
    const newExpanded = new Set(expandedSystems);
    if (newExpanded.has(systemCode)) {
      newExpanded.delete(systemCode);
    } else {
      newExpanded.add(systemCode);
    }
    setExpandedSystems(newExpanded);
  };

  const toggleSubsystem = (subsystemId: number) => {
    const newExpanded = new Set(expandedSubsystems);
    if (newExpanded.has(subsystemId)) {
      newExpanded.delete(subsystemId);
    } else {
      newExpanded.add(subsystemId);
    }
    setExpandedSubsystems(newExpanded);
  };

  const getEquipmentTypeIcon = (equipmentType: string) => {
    const type = equipmentType.toLowerCase();
    if (type.includes('instrument')) return <Engineering />;
    if (type.includes('valve')) return <Water />;
    if (type.includes('motor') || type.includes('electrical')) return <ElectricalServices />;
    if (type.includes('switch')) return <Settings />;
    return <Build />;
  };

  const getStatusColor = (status: string | undefined) => {
    if (!status) return 'default';
    switch (status) {
      case 'in_service': return 'success';
      case 'out_of_service': return 'error';
      case 'maintenance': return 'warning';
      case 'testing': return 'info';
      default: return 'default';
    }
  };

  const getCriticalityColor = (criticality: string | undefined) => {
    if (!criticality) return 'default';
    switch (criticality) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading plant information...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          🏭 Plant Information - {siteName}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<FilterList />}
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Hide' : 'Show'} Filters
        </Button>
      </Box>

      {/* Main Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(event, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
        >
          <Tab 
            label="Equipment Hierarchy" 
            icon={<Engineering />} 
            iconPosition="start"
          />
          <Tab 
            label="Document Search" 
            icon={<Description />} 
            iconPosition="start"
          />
          <Tab 
            label="Browse Documents" 
            icon={<Folder />} 
            iconPosition="start"
          />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {currentTab === 0 && (
        <>
          {/* Filters */}
          {showFilters && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Search Equipment"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Equipment Type</InputLabel>
                  <Select
                    value={selectedEquipmentType}
                    onChange={(e) => setSelectedEquipmentType(e.target.value)}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {equipmentTypes.map(type => (
                      <MenuItem key={type.id} value={type.name}>
                        {type.name} ({type.unit_code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="in_service">In Service</MenuItem>
                    <MenuItem value="out_of_service">Out of Service</MenuItem>
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="testing">Testing</MenuItem>
                    <MenuItem value="standby">Standby</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Criticality</InputLabel>
                  <Select
                    value={selectedCriticality}
                    onChange={(e) => setSelectedCriticality(e.target.value)}
                  >
                    <MenuItem value="">All Criticality</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedEquipmentType('');
                      setSelectedStatus('');
                      setSelectedCriticality('');
                    }}
                  >
                    Clear Filters
                  </Button>
                  <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                    {filteredEquipment.length} items
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total Equipment</Typography>
              <Typography variant="h4" color="primary">
                {filteredEquipment.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Units</Typography>
              <Typography variant="h4" color="primary">
                {siteUnits.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Subsystems</Typography>
              <Typography variant="h4" color="primary">
                {subsystems.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Critical Equipment</Typography>
              <Typography variant="h4" color="error">
                {filteredEquipment.filter(e => e.criticality === 'critical').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* KKS Hierarchy */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            KKS Hierarchy
          </Typography>
          
          {siteUnits.map(unit => (
            <Accordion 
              key={unit.id}
              expanded={expandedUnits.has(unit.id)}
              onChange={() => toggleUnit(unit.id)}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">
                  🏢 Unit {unit.unit_number} - {unit.name}
                </Typography>
                <Chip 
                  label={`${Object.keys(hierarchicalStructure[unit.id] || {}).length} systems`}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </AccordionSummary>
              <AccordionDetails>
                {Object.entries(hierarchicalStructure[unit.id] || {})
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([systemCode, systemGroup]) => (
                    <Accordion 
                      key={systemCode}
                      expanded={expandedSystems.has(systemCode)}
                      onChange={() => toggleSystem(systemCode)}
                      sx={{ mb: 1 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            📁 {systemGroup.kks_code} - {systemGroup.name}
                          </Typography>
                          {systemGroup.is_critical && (
                            <Chip label="Critical" color="error" size="small" />
                          )}
                          <Chip 
                            label={`${systemGroup.subsystems.length} subsystems`}
                            size="small"
                            color="primary"
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {systemGroup.subsystems.map(subsystem => (
                          <Accordion 
                            key={subsystem.id}
                            expanded={expandedSubsystems.has(subsystem.id)}
                            onChange={() => toggleSubsystem(subsystem.id)}
                            sx={{ mb: 1 }}
                          >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1">
                                  ⚙️ {subsystem.kks_code} - {subsystem.name}
                                </Typography>
                                {subsystem.is_critical && (
                                  <Chip label="Critical" color="error" size="small" />
                                )}
                                <Chip 
                                  label={`${systemGroup.equipment.filter(eq => eq.kks_code.startsWith(subsystem.kks_code)).length} equipment`}
                                  size="small"
                                  color="secondary"
                                />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <TableContainer component={Paper}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>KKS Code</TableCell>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Type</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Criticality</TableCell>
                                      <TableCell>Manufacturer</TableCell>
                                      <TableCell>Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {systemGroup.equipment
                                      .filter(item => item.kks_code.startsWith(subsystem.kks_code))
                                      .map(item => (
                                <TableRow key={item.id}>
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                      {item.kks_code}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{item.name}</TableCell>
                                  <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      {getEquipmentTypeIcon(item.equipment_unit_name || '')}
                                      <Typography variant="body2">
                                        {item.equipment_unit_name || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={item.status ? item.status.replace('_', ' ').toUpperCase() : 'UNKNOWN'}
                                      color={getStatusColor(item.status)}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={item.criticality ? item.criticality.toUpperCase() : 'UNKNOWN'}
                                      color={getCriticalityColor(item.criticality)}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>{item.manufacturer || 'N/A'}</TableCell>
                                  <TableCell>
                                    <Tooltip title="View Details">
                                      <IconButton 
                                        size="small"
                                        onClick={() => fetchEquipmentDetail(item.id)}
                                      >
                                        <Info />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                </AccordionDetails>
              </Accordion>
            ))}
        </CardContent>
      </Card>

      {/* Equipment Detail Modal */}
      <Dialog 
        open={equipmentDetailOpen} 
        onClose={() => setEquipmentDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Equipment Details: {selectedEquipment?.kks_code}
            </Typography>
            <IconButton onClick={() => setEquipmentDetailOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEquipment && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Basic Information</Typography>
                  <Typography><strong>Name:</strong> {selectedEquipment.name}</Typography>
                  <Typography><strong>KKS Code:</strong> {selectedEquipment.kks_code}</Typography>
                  <Typography><strong>Manufacturer:</strong> {selectedEquipment.manufacturer || 'N/A'}</Typography>
                  <Typography><strong>Model:</strong> {selectedEquipment.model || 'N/A'}</Typography>
                  <Typography><strong>Serial Number:</strong> {selectedEquipment.serial_number || 'N/A'}</Typography>
                  <Typography><strong>Status:</strong> {selectedEquipment.status}</Typography>
                  <Typography><strong>Criticality:</strong> {selectedEquipment.criticality}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Technical Specifications</Typography>
                  {selectedEquipment.voltage_rating && (
                    <Typography><strong>Voltage Rating:</strong> {selectedEquipment.voltage_rating}</Typography>
                  )}
                  {selectedEquipment.current_rating && (
                    <Typography><strong>Current Rating:</strong> {selectedEquipment.current_rating}</Typography>
                  )}
                  {selectedEquipment.power_rating && (
                    <Typography><strong>Power Rating:</strong> {selectedEquipment.power_rating}</Typography>
                  )}
                  {selectedEquipment.pressure_rating && (
                    <Typography><strong>Pressure Rating:</strong> {selectedEquipment.pressure_rating}</Typography>
                  )}
                  {selectedEquipment.flow_rating && (
                    <Typography><strong>Flow Rating:</strong> {selectedEquipment.flow_rating}</Typography>
                  )}
                </Grid>
              </Grid>

              {selectedEquipment.description && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>Description</Typography>
                  <Typography>{selectedEquipment.description}</Typography>
                </Box>
              )}

              {/* Enhanced Document Management */}
              <Box mt={2}>
                <EquipmentDocumentViewer
                  equipmentId={selectedEquipment.id}
                  equipmentName={selectedEquipment.name}
                  equipmentKksCode={selectedEquipment.kks_code}
                />
              </Box>

              {selectedEquipment.child_equipment && selectedEquipment.child_equipment.length > 0 && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>Child Equipment</Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>KKS Code</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedEquipment.child_equipment.map(child => (
                          <TableRow key={child.id}>
                            <TableCell>{child.kks_code}</TableCell>
                            <TableCell>{child.name}</TableCell>
                            <TableCell>{child.equipment_unit_name || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip 
                                                                      label={child.status ? child.status.replace('_', ' ').toUpperCase() : 'UNKNOWN'}
                                color={getStatusColor(child.status)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEquipmentDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
        </>
      )}

      {/* Document Search Tab */}
      {currentTab === 1 && (
        <Box sx={{ p: 2 }}>
          <DocumentSearch 
            title="Plant Document Search"
            showEquipmentFilter={true}
            onDocumentSelect={handleDocumentSelect}
          />
        </Box>
      )}

      {/* Browse Documents Tab */}
      {currentTab === 2 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Browse Documents by Category
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse and manage all plant documents organized by category and equipment.
            This feature will be enhanced with document upload and management capabilities.
          </Typography>
        </Box>
      )}

      {/* Document Viewer Dialog */}
      <DocumentViewer
        document={selectedDocument}
        open={documentViewerOpen}
        onClose={handleCloseDocumentViewer}
      />
    </Box>
  );
};

export default PlantInformation; 